import React, { useMemo } from 'react'
import { Button, makeStyles, Typography, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { getTierPrices, TIER_LABELS, TIER_ORDER } from '@paintscout/util/builder'
import type { TieredPackageTierId } from 'paintscout'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import classNames from 'classnames'
import { useQuote } from '../../../../context'
import { Pill, TierMark } from './shared'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    // Static PaintScout brand blue — not theme.palette.primary, which is the account's brand color
    // (e.g. gold) in the customer view. Used consistently for seals, checks, price and the CTA.
    const brandBlue = theme.paintscout.palette.brand.Blue.Blue55
    return {
      row: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2.5),
        [theme.breakpoints.down('xs')]: {
          gridTemplateColumns: '1fr'
        }
      },
      card: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${neutral.P90}`,
        borderRadius: theme.borderRadius.xl,
        background: neutral.White,
        overflow: 'hidden'
      },
      // The emphasized tier (recommended on the gate, selected in the switch dialog) gets a
      // brand-blue outline.
      cardHighlighted: {
        borderColor: brandBlue,
        boxShadow: `0 0 0 1px ${brandBlue}`
      },
      // Shaded header band: tier mark + name + tagline.
      header: {
        background: neutral.P98,
        borderBottom: `1px solid ${neutral.P90}`,
        padding: theme.spacing(2, 2.5)
      },
      mark: {
        marginBottom: theme.spacing(1)
      },
      name: {
        color: neutral.P30,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(18),
        lineHeight: 1.3
      },
      tagline: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14),
        marginTop: theme.spacing(0.25)
      },
      priceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${neutral.P90}`
      },
      price: {
        color: brandBlue,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(24)
      },
      recommendedPill: {
        gap: theme.spacing(0.5)
      },
      recommendedIcon: {
        fontSize: theme.typography.pxToRem(14)
      },
      featureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        padding: theme.spacing(2.5)
      },
      featureItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.4
      },
      featureIncluded: {
        color: neutral.P30
      },
      featureExcluded: {
        color: neutral.P70
      },
      checkIcon: {
        fontSize: theme.typography.pxToRem(18),
        color: brandBlue,
        flexShrink: 0
      },
      excludeIcon: {
        fontSize: theme.typography.pxToRem(18),
        color: neutral.P80,
        flexShrink: 0
      },
      // Button pinned to the bottom so cards line up regardless of feature-count.
      buttonWrap: {
        marginTop: 'auto',
        padding: theme.spacing(0, 2.5, 2.5)
      },
      button: {
        textTransform: 'none'
      },
      // Non-emphasized CTAs are outlined; MUI's outlined variant would tint them with the account's
      // brand color (theme.palette.primary — gold on some accounts), so force blue text + border to
      // keep the whole comparison blue-based rather than brand-colored.
      buttonOutlined: {
        color: brandBlue,
        borderColor: theme.paintscout.palette.brand.Blue.Blue80,
        '&:hover': {
          borderColor: brandBlue,
          backgroundColor: theme.paintscout.palette.semantic.Info.S95
        }
      },
      // The emphasized CTA is filled with PaintScout brand blue (white text) so it stays blue
      // regardless of the account's brand color (theme.palette.primary, which contained buttons use).
      buttonFilled: {
        backgroundColor: brandBlue,
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: theme.paintscout.palette.brand.Blue.Blue50
        }
      }
    }
  },
  { name: 'PackageComparisonCards' }
)

export interface PackageComparisonCardsProps {
  /**
   * `gate` — landing page before the estimate: emphasize + pill the recommended tier, CTA reads
   * "View Estimate" / "View <name>". `switch` — the "Compare plans" dialog: emphasize the currently
   * selected tier with a disabled "Selected" CTA, others read "Switch to <name>", no pill.
   */
  variant: 'gate' | 'switch'
  /** Called with the tier whose CTA was clicked. */
  onSelectTier: (tierId: TieredPackageTierId) => void
}

/**
 * The shared three-up tier comparison grid (header band with seal + name + tagline, price + optional
 * "Recommended" pill, the union of every tier's features shown ✓/✗ per tier, and a per-tier CTA).
 * Used by both the customer-facing gate ({@link TieredPackageGate}) and the "Compare plans" switch
 * dialog ({@link PackageCompareDialog}) so the two stay pixel-consistent.
 */
export default function PackageComparisonCards({ variant, onSelectTier }: PackageComparisonCardsProps) {
  const classes = useStyles({})
  const theme = useTheme()
  const { quote } = useQuote()
  const tierPrices = useMemo(() => getTierPrices({ quote }), [quote])

  // Union of every tier's features (first-seen order) so each card can show the same list with a
  // ✓ (included) or ✗ (not in this tier), giving the aligned comparison in the mock.
  const allFeatures = useMemo(() => {
    if (!tierPrices) {
      return []
    }
    const seen = new Set<string>()
    const list: string[] = []
    TIER_ORDER.forEach((tierId) => {
      ;(tierPrices[tierId]?.features ?? []).forEach((feature) => {
        const trimmed = feature.trim()
        if (trimmed && !seen.has(trimmed)) {
          seen.add(trimmed)
          list.push(trimmed)
        }
      })
    })
    return list
  }, [tierPrices])

  if (!tierPrices) {
    return null
  }

  const brandBlue = theme.paintscout.palette.brand.Blue.Blue55

  return (
    <div className={classes.row}>
      {TIER_ORDER.map((tierId) => {
        const tier = tierPrices[tierId]
        const name = tier.name || TIER_LABELS[tierId]
        const included = new Set((tier.features ?? []).map((f) => f.trim()).filter(Boolean))

        const emphasized = variant === 'gate' ? tier.isRecommended : tier.isSelected
        const showPill = variant === 'gate' && tier.isRecommended
        const buttonLabel =
          variant === 'gate'
            ? tier.isRecommended
              ? 'View Estimate'
              : `View ${name}`
            : tier.isSelected
              ? 'Selected'
              : `Switch to ${name}`

        return (
          <div key={tierId} className={classNames(classes.card, emphasized && classes.cardHighlighted)}>
            <div className={classes.header}>
              <div className={classes.mark}>
                <TierMark color={brandBlue} size={22} />
              </div>
              <Typography className={classes.name}>{name}</Typography>
              {tier.tagline && <Typography className={classes.tagline}>{tier.tagline}</Typography>}
            </div>

            <div className={classes.priceRow}>
              <Typography variant="inherit" className={classes.price} value={tier.price} format="price" showUnits />
              {showPill && (
                <Pill className={classes.recommendedPill} icon={<StarBorderIcon className={classes.recommendedIcon} />}>
                  Recommended
                </Pill>
              )}
            </div>

            {allFeatures.length > 0 && (
              <div className={classes.featureList}>
                {allFeatures.map((feature, i) => {
                  const isIncluded = included.has(feature)
                  return (
                    <div
                      key={i}
                      className={classNames(
                        classes.featureItem,
                        isIncluded ? classes.featureIncluded : classes.featureExcluded
                      )}
                    >
                      {isIncluded ? (
                        <CheckIcon className={classes.checkIcon} />
                      ) : (
                        <CloseIcon className={classes.excludeIcon} />
                      )}
                      <span>{feature}</span>
                    </div>
                  )
                })}
              </div>
            )}

            <div className={classes.buttonWrap}>
              <Button
                fullWidth
                className={classNames(classes.button, emphasized ? classes.buttonFilled : classes.buttonOutlined)}
                variant={emphasized ? 'contained' : 'outlined'}
                onClick={() => onSelectTier(tierId)}
              >
                {buttonLabel}
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
